import { useEffect, useState, useCallback } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import LinearProgress from '@mui/material/LinearProgress';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import cookie from 'cookie';
import { setCookie } from 'cookies-next';
import Router, { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';

import MainLayout from '@/layouts';
import { getDocumentTypes, getFooter, getNavBar, getPolicySidebar } from '@/services/apis/strapi';
import { darkComponents, lightComponents } from '@/theme/components';
import palette from '@/theme/palette';
import '../styles/ck.css';
import '../styles/font.css';
import '../styles/globals.css';
import '../styles/partners.css';

import { pathPolicy } from '@/utils';

// global flag to ignore programmatic replaces we initiate
let isReplacingGlobal = false;

const darkTheme = createTheme({
  palette: palette.dark,
  components: darkComponents,
});

const lightTheme = createTheme({
  palette: palette.light,
  components: lightComponents,
});

function MyApp({ Component, pageProps, footerData, headerData }) {
  const [cachedFooterData, setCachedFooterData] = useState(footerData);
  const [cachedHeaderData, setCachedHeaderData] = useState(headerData);
  const [sidebarData, setSidebarData] = useState([]);
  const [themeMode, setThemeMode] = useState('light');
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const { i18n } = useTranslation();
  const {
    utm_medium: utmMedium,
    utm_source: utmSource,
    utm_campaign: utmCampainName,
    productId,
    policyId,
  } = router.query;

  const rootPath = router?.pathname?.split('/')?.[1];

  useEffect(() => {
    const handleStart = () => setLoading(true);
    const handleStop = () => setLoading(false);

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  // Global interceptor: if user is leaving an English document page (/en/document)
  // and destination starts with `/en/`, strip the `/en` prefix and replace once.
  useEffect(() => {
    const handler = (url) => {
      try {
        if (isReplacingGlobal) {
          isReplacingGlobal = false;
          return;
        }

        if (!url) return;
        // Only act when destination starts with `/en`
        if (!url.startsWith('/en')) return;

        // If navigating from /en/document to another /en/* route, strip /en and redirect
        let stripped = url.slice(3); // remove leading '/en'
        if (!stripped) stripped = '/';
        if (!stripped.startsWith('/')) {
          stripped = `/${stripped}`;
        }

        // If destination (after stripping) is a document path, don't redirect
        if (stripped === '/document' || stripped.startsWith('/document/')) {
          return;
        }

        isReplacingGlobal = true;
        // navigate to the Vietnamese version of the stripped path
        router.replace(stripped, stripped, { locale: 'vi' });
      } catch (e) {
        /* ignore */
      }
    };

    Router.events.on('routeChangeStart', handler);
    return () => {
      Router.events.off('routeChangeStart', handler);
    };
  }, [router]);

  // On client initial mount, if URL starts with /en and is not /en/document, switch to Vietnamese
  useEffect(() => {
    try {
      let current = '';
      if (typeof window !== 'undefined') {
        current = router?.asPath || window.location?.pathname || '';
      } else {
        current = router?.asPath || '';
      }
      if (!current) return;
      if (!current.startsWith('/en')) return;

      if (current.startsWith('/en/document')) return;

      let stripped = current.slice(3); // remove leading '/en'
      if (!stripped) stripped = '/';
      if (!stripped.startsWith('/')) stripped = `/${stripped}`;

      isReplacingGlobal = true;
      router.replace(stripped, stripped, { locale: 'vi' });
    } catch (e) {
      /* ignore */
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetDataHeaderFooter = useCallback(async () => {
    if (!cachedFooterData || !cachedHeaderData) {
      try {
        const headerDataProductsNew = await getNavBar(i18n?.language);
        const footerDataNew = await getFooter(i18n?.language);

        const newFooterData = {
          infoData: footerDataNew,
        };

        if (!footerDataNew || !headerDataProductsNew) {
          return { notFound: true };
        }

        setCachedFooterData(newFooterData);
        setCachedHeaderData(headerDataProductsNew);
      } catch (error) {
        if (error?.response?.status === 404) {
          return { notFound: true };
        }
        throw error;
      }
    }
    return null;
  }, [cachedFooterData, cachedHeaderData, i18n]);

  useEffect(() => {
    handleGetDataHeaderFooter();
  }, [handleGetDataHeaderFooter]);

  const handleFetchDataSidebarDocument = async (productSlug) => {
    if (productSlug) {
      try {
        const newSidebarData = await getDocumentTypes({ productSlug, locale: i18n?.language });
        setSidebarData(newSidebarData);
      } catch (error) {
        /* empty */
      }
    }
  };
  const handleFetchDataSidebarPolicy = async () => {
    try {
      const newSidebarData = await getPolicySidebar({ locale: i18n?.language });
      setSidebarData(newSidebarData);
    } catch (error) {
      /* empty */
    }
  };

  useEffect(() => {
    handleGetDataHeaderFooter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (utmMedium && utmSource && utmCampainName) {
      setCookie('__gtm_campaign_url', `${window.location.origin.toString()}${router.asPath}`);
      setCookie('gtm_campaign', router.asPath);
    }
  }, [utmMedium, utmSource, utmCampainName, router?.asPath]);

  const theme = themeMode === 'light' ? lightTheme : darkTheme;

  const toggleThemeMode = () => {
    const newThemeMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newThemeMode);
    localStorage?.setItem('themeMode', newThemeMode);
  };

  useEffect(() => {
    handleFetchDataSidebarDocument(productId?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language, productId]);

  useEffect(() => {
    if (router?.pathname === `/${pathPolicy()}` || rootPath === pathPolicy()) {
      handleFetchDataSidebarPolicy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language, policyId]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {loading && (
        <LinearProgress
          color="primary"
          sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 2000 }}
        />
      )}
      <MainLayout
        footerData={cachedFooterData}
        headerData={cachedHeaderData}
        sidebarData={sidebarData}
        toggleThemeMode={toggleThemeMode}
      >
        <Component {...pageProps} />
      </MainLayout>
    </ThemeProvider>
  );
}

MyApp.getInitialProps = async ({ ctx }) => {
  const { req, locale } = ctx;
  const cookies = cookie.parse(req?.headers?.cookie || '');

  let footerData;
  let headerData;

  try {
    headerData = await getNavBar(locale);
    footerData = await getFooter(locale);
    const newFooterData = {
      infoData: footerData,
    };

    return {
      cookies,
      footerData: newFooterData,
      headerData,
    };
  } catch (error) {
    return { notFound: true };
  }
};

export default appWithTranslation(MyApp);
